/**
 * Everything on the maintenance page that differs between the ten apps.
 *
 * Stylesi's own frame, verified via Figma node 150:2342 (get_design_context,
 * 2026-09-23): profile A structurally (same layout constants as AliDrop —
 * glow 1570x734 at 93%, pill border 27%, rings at 781/725 of 1440), but
 * deliberately monochrome — see the `accent` note below.
 *
 * Copying this component to a sibling repo means editing this file and nothing
 * else. Values live in plans/maintenance-pages-all-apps/src/tokens.json.
 */

export type Promo = {
  name: string;
  headline: string;
  blurb: string;
  linkLabel: string;
  linkUrl: string;
};

/** Standard promo block for apps whose maintenance page promotes DropshipTool
 *  — copied verbatim from the AliDrop reference. */
export const DROPSHIPTOOL: Promo = {
  name: "Dropshiptool",
  headline: "Discover Winning Products and High-Performing Viral Ads",
  blurb:
    "Track over 3 million Shopify stores to uncover winning products, viral TikTok ads, and top-performing Facebook and Instagram creatives.",
  linkLabel: "dropshiptool.io",
  linkUrl:
    "https://app.dropshiptool.io/register?utm_source=stylesi&utm_medium=maintenance&utm_campaign=findwinningads",
};

/** Identical on every frame — kept here so the ten copies can't drift. */
export const SHARED = {
  status: "Maintenance in progress",
  statusShort: "Maintenance",
  body: "We're running scheduled work on the platform. Nothing is lost, and your store keeps selling. We'll be back shortly.",
  cta: "Back to Home",
  reassurance: "This page refreshes itself when we're live.",
  promoEyebrow: "While you wait",
};

export const APP = {
  name: "Stylesi",
  /** `${name} will be back soon.` — the only per-app string in the headline. */
  home: "/",
  promo: DROPSHIPTOOL,

  /** Headline ink. Also the source for the divider and card border. Same
   *  value as `accent` below — Stylesi's frame is monochrome. */
  ink: "#131313",
  /** Body, reassurance, eyebrow, blurb, pill label. */
  muted: "#828282",

  /**
   * Brand accent for this page ONLY — CTA fill, status dot, background glow,
   * background rings, promo link colour.
   *
   * Deliberately NOT `theme.colors.primary` (`#6056EB` in
   * `src/constants/theme.ts`, correct for the app's normal chrome). Stylesi's
   * maintenance frame (Figma node 150:2342) is monochrome by design — black
   * CTA, black logo, no purple anywhere — confirmed twice, by reading the
   * rendered frame and by get_design_context. Do NOT "fix" this back to the
   * theme token; that would visibly reintroduce purple into a frame that has
   * none.
   */
  accent: "#131313",

  /** Logo box, measured from this app's own frame. Rendered with
   *  object-fit: contain, so a wrong value letterboxes rather than distorts. */
  logo: {
    src: "/logo.svg",
    width: 123,
    height: 30.67,
    mobileWidth: 103,
    mobileHeight: 25.68,
  },

  /** Ring left offsets as a percentage of frame width (desktop 781/725 of
   *  1440; mobile 250/194 of 391 — profile A, same as AliDrop). */
  rings: { outer: 54.2, inner: 50.3, outerMobile: 63.9, innerMobile: 49.6 },

  /** Background glow. Radii are the SVG matrix scale x10 (gradientUnits is
   *  userSpaceOnUse with r=10). Stop colour was not captured for this frame
   *  (monochrome, nothing to sample) — the gradient is expressed purely off
   *  `accent`, which resolves to a neutral black-on-white wash rather than an
   *  invented hue. */
  glow: { radiusX: 1570, radiusY: 734, atX: 93, mobileRadiusX: 426 },

  /** Status pill border alpha. Two-digit hex suffix. 45 = 27%. */
  pillBorderAlpha: "45",

  /** Same weights as the AliDrop reference — both apps are on Inter. */
  font: {
    family:
      '"Inter Variable", Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    headline: 600,
    body: 400,
    cta: 700,
    reassurance: 300,
    eyebrow: 500,
    promoName: 600,
    promoHeadline: 600,
    promoBlurb: 400,
    promoLink: 600,
  },
};
